import { useNavigate } from "react-router-dom";
import { ArrowLeft, CheckCircle, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { PLATFORM_IMAGES } from "@/constants/platformImages";

export default function WixIntegration() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/settings', { replace: true })}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Settings
          </Button>
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 rounded-xl bg-background border border-border/30">
              <img src={PLATFORM_IMAGES.wix} alt="Wix" className="h-8 w-8" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-foreground">Wix Integration</h1>
              <p className="text-muted-foreground mt-2">
                Connect your Wix site to publish SEO-optimized articles directly to your Wix blog
              </p>
            </div>
          </div>
        </div>

        {/* Prerequisites */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BookOpen className="h-5 w-5" />
              Prerequisites
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">
              To enable integration with Wix, you need:
            </p>
            <ul className="list-disc list-inside mt-4 space-y-2 text-muted-foreground">
              <li>A Wix account with site access</li>
              <li>API access enabled for your Wix site</li>
            </ul>
          </CardContent>
        </Card>

        {/* Step 1 */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Step 1: Get Your Wix Credentials</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground">
              Before connecting, you need to get your Wix Site ID and API Access Token. Note that Wix uses OAuth 2.0, so you'll need an OAuth access token, not just an API key.
            </p>
            
            <div className="space-y-4">
              <div>
                <h4 className="font-semibold mb-2">Getting Your Site ID:</h4>
                <div className="space-y-2 ml-4">
                  <div className="flex items-start gap-2">
                    <Badge variant="outline" className="mt-1">1</Badge>
                    <p className="text-sm text-muted-foreground">Open your Wix site in the editor</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <Badge variant="outline" className="mt-1">2</Badge>
                    <p className="text-sm text-muted-foreground">Go to <strong>Settings</strong> → <strong>General</strong></p>
                  </div>
                  <div className="flex items-start gap-2">
                    <Badge variant="outline" className="mt-1">3</Badge>
                    <p className="text-sm text-muted-foreground">Find your <strong>Site ID</strong> (format: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx)</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <Badge variant="outline" className="mt-1">Alt</Badge>
                    <p className="text-sm text-muted-foreground">You can also find it in your site URL when editing: <code className="text-xs bg-muted px-1 py-0.5 rounded">editor.wix.com/editor/[SITE_ID]/...</code></p>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="font-semibold mb-2">Getting Your Access Token:</h4>
                <div className="space-y-2 ml-4">
                  <div className="flex items-start gap-2">
                    <Badge variant="outline" className="mt-1">1</Badge>
                    <p className="text-sm text-muted-foreground">Go to <a href="https://manage.wix.com/account/api-keys" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Wix API Keys</a> (manage.wix.com/account/api-keys)</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <Badge variant="outline" className="mt-1">2</Badge>
                    <p className="text-sm text-muted-foreground">Create a new API key or select an existing one</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <Badge variant="outline" className="mt-1">3</Badge>
                    <p className="text-sm text-muted-foreground">Ensure the API key has <strong>Blog: Read & Write</strong> permissions</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <Badge variant="outline" className="mt-1">4</Badge>
                    <p className="text-sm text-muted-foreground">Copy the <strong>API Key</strong> value (this is your access token)</p>
                  </div>
                </div>
                <div className="mt-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                  <p className="text-sm text-yellow-800 dark:text-yellow-200">
                    <strong>Important:</strong> The API key from Wix management console should work as an access token. If you get authentication errors, you may need to set up OAuth 2.0 flow instead. Check the troubleshooting section below.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Step 2 */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Step 2: Connect SEORO to Wix</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <Badge className="mt-1">1</Badge>
                <div>
                  <p className="font-medium">Log in to SEORO and navigate to Settings → Integrations</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Badge className="mt-1">2</Badge>
                <div>
                  <p className="font-medium">Under Publishing Platforms, click the Connect button next to Wix</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Badge className="mt-1">3</Badge>
                <div>
                  <p className="font-medium">In the popup:</p>
                  <ul className="list-disc list-inside mt-2 ml-4 space-y-1">
                    <li className="text-muted-foreground">Enter your Wix Site ID</li>
                    <li className="text-muted-foreground">Enter your API Access Token</li>
                  </ul>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Badge className="mt-1">4</Badge>
                <div>
                  <p className="font-medium">Click Connect</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Step 3 */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              Step 3: You Are Connected
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">
              You'll see a success message confirming your Wix integration.
            </p>
            <p className="text-sm text-muted-foreground mt-4">
              If you face any issues, contact support using the sidebar chat within the app.
            </p>
          </CardContent>
        </Card>

        {/* Publishing Workflow */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Publishing Workflow</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <p className="text-muted-foreground">
              • Articles marked as "Published" in SEORO will be immediately published to your Wix blog.
            </p>
            <p className="text-muted-foreground">
              • Make sure your Wix blog is properly configured to accept new posts.
            </p>
          </CardContent>
        </Card>

        {/* Troubleshooting */}
        <Card>
          <CardHeader>
            <CardTitle>Troubleshooting</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h4 className="font-semibold mb-2">"Failed to verify" or "400 Bad Request" Error:</h4>
              <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground ml-4">
                <li>Verify your Site ID is correct (UUID format: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx)</li>
                <li>Ensure your API key has Blog read/write permissions</li>
                <li>Make sure you're using the API Key value, not the Account ID</li>
                <li>Check that your Wix site has a Blog section added</li>
                <li>Try regenerating your API key if the issue persists</li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-2">"Invalid access token" Error:</h4>
              <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground ml-4">
                <li>Wix API keys from the management console should work directly</li>
                <li>If you're using OAuth, ensure you're using the access token, not the refresh token</li>
                <li>Verify the token hasn't expired (OAuth tokens may expire)</li>
                <li>Check that the API key is associated with the correct Wix account</li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-2">"Site not found" Error:</h4>
              <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground ml-4">
                <li>Double-check your Site ID matches the site you're trying to connect</li>
                <li>Ensure the Site ID is from the same Wix account as your API key</li>
                <li>Verify the site is published and active</li>
              </ul>
            </div>

            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <p className="text-sm text-blue-800 dark:text-blue-200">
                <strong>Need Help?</strong> If you continue to experience issues, check the Wix API documentation or contact support. The integration tries multiple API endpoint formats automatically to find the correct one for your Wix setup.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

